import { useMutation } from "@tanstack/react-query";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { hasAccounts, useMe } from "@/lib/account";
import { useTRPC } from "@/lib/trpc";

/**
 * "Share anonymous usage statistics" (ADR 0016; docs/admin-app/figures.md › Interactions): off
 * until someone turns it on, with what is counted and what never is. Hosted servers only.
 */
export function ShareUsageSwitch({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <div className="flex items-start gap-4">
      <div className="min-w-0 flex-1">
        <label htmlFor={id} className="text-sm font-medium">
          {t("shareUsage.title")}
        </label>
        <p className="text-sm text-pretty text-muted-foreground">{t("shareUsage.body")}</p>
      </div>
      <Switch id={id} checked={checked} disabled={disabled} onCheckedChange={onChange} />
    </div>
  );
}

/** The switch as the profile shows it, saved at once. */
export function ShareUsageSetting() {
  const trpc = useTRPC();
  const { data: me } = useMe();
  const save = useMutation(trpc.account.setShareUsage.mutationOptions());
  if (!hasAccounts() || !me) return null;
  return (
    <ShareUsageSwitch
      checked={me.shareUsage === true}
      disabled={save.isPending}
      onChange={(value) => save.mutate({ value })}
    />
  );
}

/**
 * Asks someone who signed up before the switch existed, once: "No thanks" is an answer, so the
 * card doesn't come back.
 */
export function ShareUsageCard() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const { data: me } = useMe();
  const save = useMutation(trpc.account.setShareUsage.mutationOptions());
  if (!hasAccounts() || me?.status !== "active" || me.shareUsage !== null) return null;
  return (
    <Card className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
      <div className="min-w-0 flex-1">
        <h2 className="text-sm font-semibold">{t("shareUsage.cardTitle")}</h2>
        <p className="text-sm text-pretty text-muted-foreground">{t("shareUsage.body")}</p>
      </div>
      <div className="flex shrink-0 gap-2">
        <Button
          variant="ghost"
          disabled={save.isPending}
          onClick={() => save.mutate({ value: false })}
        >
          {t("shareUsage.noThanks")}
        </Button>
        <Button disabled={save.isPending} onClick={() => save.mutate({ value: true })}>
          {t("shareUsage.share")}
        </Button>
      </div>
    </Card>
  );
}
